import { useEffect, useRef, useState } from "react";

import { messagingRequests } from "@/api/(requests)/messaging";
import type { PatientProfile } from "@/core/types";

import type { MessagingUser, SendBirdContactsMap } from "../types";
import { processSendBirdChannel } from "../helpers/process-channel";
import { processSendBirdMembers } from "../helpers/process-members";

export function useSendBird({ profile }: { profile: PatientProfile | undefined }): {
  sendBirdContacts: SendBirdContactsMap | null;
} {
  const isMountedRef = useRef(false);
  const clientRef = useRef<SendBird.SendBirdInstance | null>(null);
  const [sendBirdContacts, setSendBirdContacts] = useState<SendBirdContactsMap | null>(null);

  useEffect(() => {
    isMountedRef.current = true;

    if (profile?.care_team_members) {
      (async () => {
        try {
          const user = (await messagingRequests.fetch_user({
            context: { showLoader: false },
          })) as unknown as MessagingUser;
          const client = messagingRequests.create_client();

          if (!isMountedRef.current) return;
          clientRef.current = client;

          await messagingRequests.connect_to_sendbird(client, user.user_id, user.access_token);
          const { channels } = await messagingRequests.get_paginated_channels(client);
          const contacts = channels
            .map((channel: SendBird.GroupChannel) => processSendBirdChannel(user, channel))
            .reduce(processSendBirdMembers, {} as SendBirdContactsMap);

          if (!isMountedRef.current) return;
          setSendBirdContacts(Object.keys(contacts).length > 0 ? contacts : {});
        } catch (e) {
          console.log(e);
        }
      })();
    }

    return () => {
      isMountedRef.current = false;
      if (clientRef.current) {
        clientRef.current.removeAllChannelHandlers();
        clientRef.current.disconnect();
      }
    };
    // profile only — adding poll-driven values here causes full disconnect/reconnect on every tick
  }, [profile]);

  return { sendBirdContacts };
}
